import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  Webhook, Plus, Copy, Check, RefreshCw, Trash2, Send, PauseCircle, PlayCircle,
  AlertTriangle, KeyRound, ChevronDown, ChevronRight, Settings2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const STATUS_CLS = {
  delivered: 'bg-emerald-100 text-emerald-700',
  pending: 'bg-slate-100 text-slate-700',
  pending_retry: 'bg-amber-100 text-amber-700',
  failed_permanent: 'bg-red-100 text-red-700',
};

export default function WebhooksSection({ siteId }) {
  const [loading, setLoading] = useState(true);
  const [webhooks, setWebhooks] = useState([]);
  const [events, setEvents] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [secretDialog, setSecretDialog] = useState(null);
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    if (!siteId) return;
    try {
      const r = await axios.get(`${API}/portal/sites/${siteId}/webhooks`, { headers: authHeader() });
      setWebhooks(r.data?.data || []);
      setEvents(r.data?.events || []);
    } catch (e) {
      if (e?.response?.status !== 403) toast.error('Webhooks konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (wh) => {
    if (!window.confirm(`Webhook "${wh.label}" wirklich löschen?`)) return;
    try {
      await axios.delete(`${API}/portal/sites/${siteId}/webhooks/${wh.webhook_id}`, { headers: authHeader() });
      toast.success('Webhook gelöscht');
      load();
    } catch (e) {
      toast.error('Löschen fehlgeschlagen');
    }
  };

  const togglePause = async (wh) => {
    const next = wh.status === 'active' ? 'paused' : 'active';
    try {
      await axios.patch(`${API}/portal/sites/${siteId}/webhooks/${wh.webhook_id}`, { status: next }, { headers: authHeader() });
      load();
    } catch (e) { toast.error('Status-Änderung fehlgeschlagen'); }
  };

  const rotateSecret = async (wh) => {
    if (!window.confirm('Secret rotieren? Altes Secret wird sofort ungültig.')) return;
    try {
      const r = await axios.post(`${API}/portal/sites/${siteId}/webhooks/${wh.webhook_id}/rotate-secret`, {}, { headers: authHeader() });
      setSecretDialog({ secret: r.data.secret, label: wh.label, mode: 'rotated' });
      load();
    } catch (e) { toast.error('Rotation fehlgeschlagen'); }
  };

  const testWebhook = async (wh) => {
    try {
      await axios.post(`${API}/portal/sites/${siteId}/webhooks/${wh.webhook_id}/test`, {}, { headers: authHeader() });
      toast.success('Test-Event in Warteschlange gestellt');
      setTimeout(load, 1200);
    } catch (e) { toast.error('Test fehlgeschlagen'); }
  };

  if (loading) return null;

  return (
    <Card data-testid="webhooks-section">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <div>
          <CardTitle className="text-lg flex items-center gap-2">
            <Webhook className="h-5 w-5 text-orange-500" /> Webhooks
          </CardTitle>
          <CardDescription>
            HMAC-signierte Events an Deine Systeme pushen · automatische Retries · volle Lieferhistorie
          </CardDescription>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="bg-orange-500 hover:bg-orange-600" data-testid="webhook-create-btn">
          <Plus className="h-4 w-4 mr-2" /> Webhook hinzufügen
        </Button>
      </CardHeader>
      <CardContent>
        {webhooks.length === 0 ? (
          <div className="rounded-lg border border-dashed p-8 text-center" data-testid="webhooks-empty">
            <Webhook className="h-8 w-8 text-slate-400 mx-auto mb-3" />
            <p className="font-medium">Noch keine Webhooks konfiguriert</p>
            <p className="text-sm text-muted-foreground mt-1">
              Empfange z.&nbsp;B. <code className="text-xs">order.created</code>, <code className="text-xs">product.updated</code>,
              oder <code className="text-xs">commission.paid</code> in Echtzeit.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {webhooks.map(wh => (
              <WebhookRow
                key={wh.webhook_id}
                webhook={wh}
                expanded={expanded === wh.webhook_id}
                onToggleExpand={() => setExpanded(expanded === wh.webhook_id ? null : wh.webhook_id)}
                siteId={siteId}
                onEdit={() => setEditEntry(wh)}
                onDelete={() => remove(wh)}
                onPause={() => togglePause(wh)}
                onRotate={() => rotateSecret(wh)}
                onTest={() => testWebhook(wh)}
              />
            ))}
          </div>
        )}
      </CardContent>

      <WebhookCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        siteId={siteId}
        events={events}
        onCreated={(secret, webhook) => { setSecretDialog({ secret, label: webhook.label, mode: 'created' }); load(); setCreateOpen(false); }}
      />

      <WebhookEditDialog
        entry={editEntry}
        onClose={() => setEditEntry(null)}
        siteId={siteId}
        events={events}
        onSaved={() => { setEditEntry(null); load(); }}
      />

      <WebhookSecretDialog entry={secretDialog} onClose={() => setSecretDialog(null)} />
    </Card>
  );
}

// ─── Row ──────────────────────────────────────────────────────────────

function WebhookRow({ webhook, expanded, onToggleExpand, siteId, onEdit, onDelete, onPause, onRotate, onTest }) {
  const [deliveries, setDeliveries] = useState([]);
  const [loadingDeliveries, setLoadingDeliveries] = useState(false);

  const loadDeliveries = useCallback(async () => {
    setLoadingDeliveries(true);
    try {
      const r = await axios.get(`${API}/portal/sites/${siteId}/webhooks/${webhook.webhook_id}/deliveries?limit=30`, { headers: authHeader() });
      setDeliveries(r.data?.data || []);
    } catch (e) { /* silent */ } finally { setLoadingDeliveries(false); }
  }, [siteId, webhook.webhook_id]);

  useEffect(() => { if (expanded) loadDeliveries(); }, [expanded, loadDeliveries]);

  const redeliver = async (d) => {
    try {
      await axios.post(`${API}/portal/sites/${siteId}/webhooks/${webhook.webhook_id}/deliveries/${d.delivery_id}/redeliver`, {}, { headers: authHeader() });
      toast.success('Re-Delivery gestartet');
      setTimeout(loadDeliveries, 1200);
    } catch (e) { toast.error('Re-Delivery fehlgeschlagen'); }
  };

  const statusColor = webhook.status === 'active'
    ? (webhook.consecutive_failures >= 3 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700')
    : 'bg-slate-100 text-slate-600';

  return (
    <div className="border rounded-lg" data-testid={`webhook-row-${webhook.webhook_id}`}>
      <div className="flex items-center gap-3 p-3">
        <button onClick={onToggleExpand} className="text-slate-500 hover:text-slate-800" data-testid={`webhook-expand-${webhook.webhook_id}`}>
          {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium truncate">{webhook.label}</p>
            <Badge className={statusColor}>{webhook.status === 'active' ? 'Aktiv' : 'Pausiert'}</Badge>
            {webhook.consecutive_failures >= 3 && (
              <Badge variant="destructive" className="text-[10px]">{webhook.consecutive_failures} Fehler in Folge</Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground font-mono truncate mt-0.5">{webhook.url}</p>
          <div className="flex gap-1 flex-wrap mt-1">
            {(webhook.events || []).slice(0, 5).map(e => (
              <span key={e} className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 text-slate-700 rounded">{e}</span>
            ))}
            {(webhook.events || []).length > 5 && <span className="text-[10px] text-muted-foreground">+{webhook.events.length - 5}</span>}
          </div>
        </div>
        <div className="flex gap-1 flex-wrap justify-end">
          <Button size="sm" variant="outline" onClick={onTest} data-testid={`webhook-test-btn-${webhook.webhook_id}`} title="Test-Event senden">
            <Send className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={onPause} data-testid={`webhook-pause-btn-${webhook.webhook_id}`} title={webhook.status === 'active' ? 'Pausieren' : 'Aktivieren'}>
            {webhook.status === 'active' ? <PauseCircle className="h-3.5 w-3.5" /> : <PlayCircle className="h-3.5 w-3.5" />}
          </Button>
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`webhook-edit-btn-${webhook.webhook_id}`}>
            <Settings2 className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" onClick={onRotate} data-testid={`webhook-rotate-btn-${webhook.webhook_id}`} title="Secret rotieren">
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="outline" className="text-red-600" onClick={onDelete} data-testid={`webhook-delete-btn-${webhook.webhook_id}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {expanded && (
        <div className="border-t bg-slate-50/60 p-3" data-testid={`webhook-deliveries-${webhook.webhook_id}`}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Lieferungen (letzte 30)</p>
            <Button size="sm" variant="ghost" onClick={loadDeliveries} className="h-7 text-xs">
              <RefreshCw className="h-3 w-3 mr-1" /> Neu laden
            </Button>
          </div>
          {loadingDeliveries ? (
            <p className="text-xs text-muted-foreground">Lade…</p>
          ) : deliveries.length === 0 ? (
            <p className="text-xs text-muted-foreground py-3">Noch keine Lieferungen. Klicke <Send className="h-3 w-3 inline" /> um ein Test-Event zu senden.</p>
          ) : (
            <div className="space-y-1.5 max-h-72 overflow-y-auto">
              {deliveries.map(d => (
                <div key={d.delivery_id} className="flex items-center justify-between gap-2 p-2 bg-white rounded border text-xs" data-testid={`delivery-${d.delivery_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className={`${STATUS_CLS[d.status] || ''} text-[10px]`}>{d.status}</Badge>
                      <code className="text-[11px]">{d.event}</code>
                      {d.last_status_code && <span className="text-muted-foreground">HTTP {d.last_status_code}</span>}
                      <span className="text-muted-foreground">· {d.attempts} Versuch{d.attempts === 1 ? '' : 'e'}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-0.5 font-mono truncate">
                      {new Date(d.created_at).toLocaleString('de-DE')}
                      {d.last_error && ` · ${d.last_error}`}
                    </p>
                  </div>
                  {d.status !== 'delivered' && (
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => redeliver(d)} data-testid={`delivery-redeliver-${d.delivery_id}`}>
                      Re-Deliver
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Create Dialog ────────────────────────────────────────────────────

function WebhookCreateDialog({ open, onOpenChange, siteId, events, onCreated }) {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) { setLabel(''); setUrl(''); setDescription(''); setSelected([]); }
  }, [open]);

  const toggle = (e) => setSelected(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);
  const toggleDomain = (prefix) => {
    const wildcard = `${prefix}.*`;
    setSelected(prev => prev.includes(wildcard) ? prev.filter(x => x !== wildcard) : [...prev.filter(x => !x.startsWith(`${prefix}.`) && x !== wildcard), wildcard]);
  };

  const submit = async () => {
    if (!label.trim()) { toast.error('Label erforderlich'); return; }
    if (!/^https?:\/\//.test(url)) { toast.error('URL muss mit http:// oder https:// beginnen'); return; }
    if (selected.length === 0) { toast.error('Mindestens ein Event wählen'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/portal/sites/${siteId}/webhooks`, {
        label: label.trim(), url: url.trim(), events: selected, description: description.trim() || null,
      }, { headers: authHeader() });
      onCreated(r.data.secret, r.data.webhook);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erstellen fehlgeschlagen');
    } finally { setSubmitting(false); }
  };

  // Group events by domain
  const grouped = {};
  events.forEach(e => {
    const [d] = e.event.split('.');
    grouped[d] = grouped[d] || [];
    grouped[d].push(e);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="webhook-create-dialog">
        <DialogHeader>
          <DialogTitle>Webhook hinzufügen</DialogTitle>
          <DialogDescription>
            Wir senden jedes abonnierte Event per HTTP POST an Deine URL · HMAC-SHA256-Signatur im
            <code className="text-xs mx-1">X-Surplify-Signature</code> Header.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Label</Label>
            <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="z.B. ERP Produktiv" data-testid="webhook-label-input" />
          </div>
          <div>
            <Label>Empfänger-URL</Label>
            <Input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://api.ihre-firma.de/surplify-hook" data-testid="webhook-url-input" />
          </div>
          <div>
            <Label>Beschreibung (optional)</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="z.B. Order-Sync in SAP" rows={2} data-testid="webhook-desc-input" />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Events abonnieren</Label>
              <p className="text-[10px] text-muted-foreground">{selected.length} ausgewählt</p>
            </div>
            <div className="space-y-3 border rounded-md p-3 bg-slate-50/50 max-h-64 overflow-y-auto">
              {Object.entries(grouped).map(([domain, items]) => {
                const wildcard = `${domain}.*`;
                const isWild = selected.includes(wildcard);
                return (
                  <div key={domain}>
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{domain}</p>
                      <button
                        type="button"
                        onClick={() => toggleDomain(domain)}
                        className="text-[10px] text-orange-600 hover:underline"
                        data-testid={`webhook-event-wildcard-${domain}`}
                      >
                        {isWild ? '★ alle ausgewählt' : 'alle abonnieren'}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 mt-1">
                      {items.map(e => (
                        <label key={e.event} className={`flex items-start gap-2 text-xs cursor-pointer p-1.5 rounded ${isWild ? 'opacity-40' : 'hover:bg-white'}`}>
                          <Checkbox
                            checked={isWild || selected.includes(e.event)}
                            disabled={isWild}
                            onCheckedChange={() => toggle(e.event)}
                            data-testid={`webhook-event-${e.event}`}
                            className="mt-0.5"
                          />
                          <span>
                            <code className="font-mono text-[11px]">{e.event}</code>
                            <p className="text-[10px] text-muted-foreground">{e.description}</p>
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={submitting} className="bg-orange-500 hover:bg-orange-600" data-testid="webhook-create-submit">
            {submitting ? 'Erstelle…' : 'Webhook erstellen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edit Dialog ──────────────────────────────────────────────────────

function WebhookEditDialog({ entry, onClose, siteId, events, onSaved }) {
  const [label, setLabel] = useState('');
  const [url, setUrl] = useState('');
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (entry) {
      setLabel(entry.label || '');
      setUrl(entry.url || '');
      setSelected(entry.events || []);
    }
  }, [entry]);

  if (!entry) return null;

  const toggle = (e) => setSelected(prev => prev.includes(e) ? prev.filter(x => x !== e) : [...prev, e]);

  const save = async () => {
    if (selected.length === 0) { toast.error('Mindestens ein Event wählen'); return; }
    try {
      await axios.patch(`${API}/portal/sites/${siteId}/webhooks/${entry.webhook_id}`, {
        label: label.trim(), url: url.trim(), events: selected,
      }, { headers: authHeader() });
      toast.success('Webhook aktualisiert');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="webhook-edit-dialog">
        <DialogHeader>
          <DialogTitle>Webhook bearbeiten</DialogTitle>
          <DialogDescription>ID: <code className="text-xs font-mono">{entry.webhook_id}</code></DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Label</Label>
            <Input value={label} onChange={e => setLabel(e.target.value)} data-testid="webhook-edit-label" />
          </div>
          <div>
            <Label>URL</Label>
            <Input value={url} onChange={e => setUrl(e.target.value)} data-testid="webhook-edit-url" />
          </div>
          <div>
            <Label>Events</Label>
            <div className="border rounded-md p-3 max-h-60 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1">
              {events.map(e => (
                <label key={e.event} className="flex items-start gap-2 text-xs cursor-pointer p-1 rounded hover:bg-slate-50">
                  <Checkbox checked={selected.includes(e.event)} onCheckedChange={() => toggle(e.event)} data-testid={`webhook-edit-event-${e.event}`} className="mt-0.5" />
                  <code className="font-mono text-[11px]">{e.event}</code>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button onClick={save} className="bg-orange-500 hover:bg-orange-600" data-testid="webhook-edit-save">Speichern</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Secret Dialog ────────────────────────────────────────────────────

function WebhookSecretDialog({ entry, onClose }) {
  const [copied, setCopied] = useState(false);
  const [ack, setAck] = useState(false);
  if (!entry) return null;

  const copy = async () => {
    await navigator.clipboard.writeText(entry.secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o && ack) onClose(); else if (!o) toast.error('Bitte speichere das Secret sicher ab.'); }}>
      <DialogContent className="max-w-lg" data-testid="webhook-secret-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-orange-500" /> {entry.mode === 'rotated' ? 'Secret rotiert' : 'Webhook erstellt'}
          </DialogTitle>
          <DialogDescription>
            Dieses Secret wird nur <strong>jetzt einmal</strong> angezeigt. Du brauchst es, um die
            HMAC-Signatur (<code className="text-xs">X-Surplify-Signature</code>) zu verifizieren.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-amber-800">
              {entry.mode === 'rotated'
                ? 'Das vorherige Secret ist sofort ungültig. Stelle sicher, dass alle empfangenden Systeme das neue Secret kennen.'
                : `Speichere das Secret sicher bei "${entry.label}". Es kann später nur rotiert, aber nicht erneut angezeigt werden.`}
            </p>
          </div>
          <div className="bg-slate-950 text-slate-100 p-3 rounded-lg font-mono text-xs break-all" data-testid="webhook-secret-value">
            {entry.secret}
          </div>
          <Button variant="outline" onClick={copy} className="w-full" data-testid="webhook-secret-copy">
            {copied ? <><Check className="h-4 w-4 mr-2 text-emerald-600" /> Kopiert</> : <><Copy className="h-4 w-4 mr-2" /> In Zwischenablage kopieren</>}
          </Button>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={ack} onCheckedChange={setAck} data-testid="webhook-secret-ack" />
            <span>Ich habe das Secret sicher gespeichert.</span>
          </label>
        </div>
        <DialogFooter>
          <Button onClick={onClose} disabled={!ack} className="bg-orange-500 hover:bg-orange-600" data-testid="webhook-secret-close">Fertig</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
